import { useEffect, useState } from "react";
import api from "@/lib/api";

/**
 * Hook to fetch the current user's accessible areas, roles and permitted actions.
 * Returns { loading, areas, roles, permissions, can(action), hasAccess, error }.
 *
 * Cached in module scope so multiple components can share one fetch per session.
 */
let cached = null;
let inflight = null;

export function clearAccessCache() {
  cached = null;
  inflight = null;
}

async function fetchAccess() {
  if (cached) return cached;
  if (inflight) return inflight;
  inflight = api
    .get("/auth/access")
    .then((r) => {
      cached = r.data;
      inflight = null;
      return cached;
    })
    .catch((err) => {
      inflight = null;
      throw err;
    });
  return inflight;
}

export function useAccess() {
  const [state, setState] = useState({
    loading: true,
    areas: [],
    roles: [],
    permissions: [],
    hasAccess: false,
    error: null,
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchAccess();
        if (cancelled) return;

        setState({
          loading: false,
          areas: data.areas || [],
          roles: data.roles || [],
          permissions: data.permissions || [],
          hasAccess: !!data.has_access,
          error: null,
        });
      } catch (err) {
        if (cancelled) return;
        setState({
          loading: false,
          areas: [],
          roles: [],
          permissions: [],
          hasAccess: false,
          error: err?.response?.data?.detail || "Failed to verify access",
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // can("po.approve") — action names come from PERMISSIONS in backend/routes/roles.py
  const can = (action) => state.permissions.includes(action);
  return { ...state, can };
}

/** Landing URL for each workspace (area keys come from ROLE_AREAS in backend/server.py). */
export const AREA_HOME = {
  sales: "/sales/dashboard",
  invoice: "/invoices",
  inventory: "/inventory",
};

/** True if the current URL is under the given area prefix. */
export function pathIsArea(pathname, area) {
  if (area === "sales") return pathname.startsWith("/sales");
  if (area === "inventory") return pathname.startsWith("/inventory");
  if (area === "invoice") {
    return (
      pathname.startsWith("/invoices") ||
      pathname.startsWith("/products") ||
      pathname.startsWith("/settings")
    );
  }
  return false;
}
